import {
  Checkbox,
  CircularProgress,
  Fade,
  FormControl,
  FormControlLabel,
  Grid,
  SelectChangeEvent,
  useMediaQuery,
} from '@mui/material';
import Box from '@mui/material/Box';
import cn from 'classnames';
import React, {
  ChangeEvent,
  FC,
  Fragment,
  memo,
  useEffect,
  useState,
} from 'react';
import { useParams } from 'react-router-dom';
import { useLocalStorage } from 'usehooks-ts';

import { CatalogItemsSort } from '../../api/def_client_api';
import bgGray from '../../assets/images/bg-gray.jpg';
import { useActions } from '../../hooks/useActions';
import { useCatalog } from '../../hooks/useCatalog';
import { useLayout } from '../../hooks/useLayout';
import { useUser } from '../../hooks/useUser';
import { ICatalogItem } from '../../shared/types/catalog-items';
import { fetchCatalogItems } from '../../store/catalog/catalog.actions';
import { CustomButton } from '../Button';
import { CatalogItem } from '../CatalogItem';
import { Icon } from '../Icon';
import { CustomMenu } from '../Mobile/Menu';
import GridCatalogCardSkeleton from '../Skeleton/GridCatalogCardSkeleton';
import ListCatalogCardSkeleton from '../Skeleton/ListCatalogCardSkeleton';
import { Sorting } from '../Sorting';

//Иконка сортировки
const SortIcon = () => (
  <svg
    width="10"
    height="6"
    viewBox="0 0 10 6"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M9 1L5 5L1 1"
      stroke="#949494"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

interface ICatalogItemsProps {
  type?: string;
  items: ICatalogItem[] | any;
  button?: boolean | undefined;
  priceKopecksMax?: number;
  priceKopecksMin?: number;
}

const catalogSort = [
  { sort: CatalogItemsSort.PriceAsc, text: 'Сначала недорогие' },
  { sort: CatalogItemsSort.PriceDesc, text: 'Сначала дорогие' },
  { sort: CatalogItemsSort.UpdatedDesc, text: 'Сначала актуальные' },
];

export const CatalogItems: FC<ICatalogItemsProps> = memo(
  ({ type, items, button, priceKopecksMax, priceKopecksMin }) => {
    const { slug, catalogType } = useParams();
    const {
      setAvailabilityOrNo,
      setIsChange,
      setOffset,
      setSelectSort,
      setShowMoreButton,
      setVisibleMobileMenu,
      setMenuVisible,
      fetchCatalogItems,
    } = useActions();
    const {
      offset,
      loading,
      newItemsLength,
      selectSort,
      availabilityOrNo,
      showMoreButton,
      catalog,
    } = useCatalog();
    const { visibleMenuDialog } = useUser();
    const { page } = useLayout();

    //Максимальная ширина экрана. Возвращает - true
    const mobileScreen = useMediaQuery('(max-width:900px)');

    const [switchActive, setSwitchActive] = useLocalStorage(
      'SWITCH_IN_CATALOG',
      'grid'
    );
    //Загрузка
    const [fetching, setFetching] = useState(false);
    //Открыть меню фильтров на моб.версиях
    const [openCategory, setOpenCategory] = useState('processes');

    //Изменить сортировку каталага
    const handleChangeSelect = (event: SelectChangeEvent) => {
      event.preventDefault();
      // @ts-ignore
      setOffset(0);
      setIsChange(true);
      setSelectSort(event.target.value as CatalogItemsSort);
    };

    //Показать еще
    const showMore = () => {
      setIsChange(false);
      setOffset(offset + 30);
      setShowMoreButton(true);
      setFetching(true);
      /*    await getData(true);*/
    };

    const { availability, no } = availabilityOrNo;
    //Если убрать "в налачии" и "нет в наличии", то поставить галочки автоматически
    if (!availabilityOrNo.availability && !availabilityOrNo.no) {
      setAvailabilityOrNo({
        availability: true,
        no: true,
      });
    }
    //Изменение "в наличии" и "нет в наличии"
    const handleChangeAvailabilityOrNo = (
      event: ChangeEvent<HTMLInputElement>
    ) => {
      setOffset(0);
      setIsChange(true);

      setAvailabilityOrNo({
        ...availabilityOrNo,
        [event.target.name]: event.target.checked,
      });
    };
    //Изменить состояние переключателя в "список" и в "сетку"
    const onCatalogSwitch = (name: 'grid' | 'list') => {
      setSwitchActive(name);
    };
    //Открыть меню фильтров на моб.версиях
    const toggleMenuDialog = (category: string) => {
      //Откыть  меню фильтры
      window.scrollTo({ top: 0, behavior: 'auto' });
      setVisibleMobileMenu(true);
      setMenuVisible(!visibleMenuDialog);
      setOpenCategory(category);
    };
    useEffect(() => {
      const taxonomy_type =
        catalogType === 'materials'
          ? 'Knowledge::Material'
          : catalogType === 'equipment' || catalogType === 'equipments'
          ? 'Knowledge::Equipment'
          : 'Knowledge::Process';
      if (slug) {
        fetchCatalogItems({
          offset,
          priceKopecksMax,
          priceKopecksMin,
          taxonomy_type,
          slug,
          availability,
          selectSort,
        });
      }
    }, [
      priceKopecksMax,
      priceKopecksMin,
      offset,
      catalogType,
      slug,
      availability,
      selectSort,
    ]);

    return (
      <div className={cn('catalog-page-center')}>
        <div className={cn('catalog-page-center--top', switchActive)}>
          <div className={cn('catalog-page-center--top__left', switchActive)}>
            <Sorting
              sorts={catalogSort}
              change={handleChangeSelect}
              switchActive={switchActive}
              icon={SortIcon}
              select={selectSort}
              page={page}
            />
            {
              //Если это не моб версия, то показывать фильтрацию
              !mobileScreen && (
                <Box sx={{ display: 'flex' }} className="catalog_filter">
                  <FormControl
                    sx={{ m: 3 }}
                    component="fieldset"
                    variant="standard"
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={availability}
                          checkedIcon={<Icon variant="checkWhite" />}
                          icon={<Icon variant="noCheckGray" />}
                          onChange={handleChangeAvailabilityOrNo}
                          name="availability"
                        />
                      }
                      label="В наличии"
                    />
                  </FormControl>
                  <FormControl
                    required
                    component="fieldset"
                    sx={{ m: 3 }}
                    variant="standard"
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={no}
                          checkedIcon={<Icon variant="checkWhite" />}
                          icon={<Icon variant="noCheckGray" />}
                          onChange={handleChangeAvailabilityOrNo}
                          name="no"
                        />
                      }
                      label="Нет в наличии"
                    />
                  </FormControl>
                </Box>
              )
            }
          </div>

          <div className={cn('catalog-page-center--right', switchActive)}>
            {!mobileScreen ? (
              <div className="switch">
                <div onClick={() => onCatalogSwitch('list')}>
                  <Icon
                    variant={
                      switchActive === 'list'
                        ? 'listCatalogActive'
                        : 'listCatalog'
                    }
                  />
                </div>
                <div onClick={() => onCatalogSwitch('grid')}>
                  <Icon
                    variant={
                      switchActive === 'grid'
                        ? 'gridCatalogActive'
                        : 'gridCatalog'
                    }
                  />
                </div>
              </div>
            ) : (
              <Fragment>
                <h2
                  onClick={() => {
                    toggleMenuDialog('materials');
                  }}
                >
                  + Фильтры
                </h2>

                <CustomMenu openCategory={openCategory} catalog />
              </Fragment>
            )}
          </div>
        </div>
        {catalog.cover_img && (
          <div className="processes__bottom_img">
            <img
              className="processes__bottom_bg_img"
              src={bgGray}
              alt="bgGray"
            />
            <img
              className="processes__bottom_cover_img"
              src={catalog.cover_img}
              alt="cover_img"
            />
          </div>
        )}
        <Fade in={fetching ? true : !loading} timeout={1000}>
          <Grid
            container
            rowGap={{ xs: 1, md: 8 }}
            columnSpacing={{ xs: 1, md: 0 }}
          >
            {
              //Если товар есть, то показываем
              switchActive === 'grid' &&
                items !== null &&
                items?.map((item: any, i: any) => {
                  return (
                    <Grid
                      key={`${item.id}_${i}`}
                      item
                      xs={6}
                      sm={4}
                      md={page === 'catalog' ? 6 : 4}
                      lg={page === 'catalog' ? 4 : 3}
                    >
                      <CatalogItem
                        screen={mobileScreen ? 'mobile' : 'desktop'}
                        item={item}
                        className={switchActive}
                      />
                    </Grid>
                  );
                })
            }
          </Grid>
        </Fade>
        <Fade in={fetching ? true : !loading} timeout={1000}>
          <Grid
            container
            rowGap={{ xs: 1, md: 1 }}
            columnSpacing={{ xs: 1, md: 0 }}
          >
            {
              //Если товар есть, то показываем
              switchActive === 'list' &&
                items !== null &&
                items?.map((item: any, i: any) => {
                  return (
                    <Grid
                      item
                      sx={{ marginTop: '8px' }}
                      key={`${item.id}_${i}`}
                      xs={12}
                      sm={6}
                      md={12}
                      lg={12}
                    >
                      <CatalogItem
                        screen={mobileScreen ? 'mobile' : 'desktop'}
                        item={item}
                        className={switchActive}
                      />
                    </Grid>
                  );
                })
            }
          </Grid>
        </Fade>
        {
          //Если нажимаем на кнопку "показать еще"
          fetching && loading && (
            <Fragment>
              <Grid
                container
                rowGap={{ xs: 1, md: 1 }}
                columnSpacing={{ xs: 1, md: 1 }}
              >
                {Array(6)
                  .fill(0)
                  .map((_, i) =>
                    switchActive === 'grid' ? (
                      <GridCatalogCardSkeleton key={i} />
                    ) : (
                      <ListCatalogCardSkeleton key={i} />
                    )
                  )}
              </Grid>
            </Fragment>
          )
        }

        {
          //Если товара нет и если товар грузится
          loading && items === null && (
            <Grid
              container
              rowGap={{ xs: 1, md: 8 }}
              columnSpacing={{ xs: 1, md: 1 }}
            >
              {Array(9)
                .fill(0)
                .map((item, i) => {
                  if (switchActive === 'grid') {
                    return <GridCatalogCardSkeleton key={i} />;
                  } else {
                    return <ListCatalogCardSkeleton key={i} />;
                  }
                })}
            </Grid>
          )
        }

        {
          //Если товара нет
          items && items.length === 0 && <div>Товар отсутствует...</div>
        }
        {/*newItemsLength > 29 && newItemsLength !== 0 &&*/}
        {button && newItemsLength > 29 && newItemsLength !== 0 && (
          <CustomButton
            onClick={showMore}
            className="button_show-more"
            variant="outlined"
          >
            Показать больше товаров
          </CustomButton>
        )}
      </div>
    );
  }
);